/**
 * WalkerRig — the code cutout rig, ported to Phaser.
 *
 * This replaces the generated walk spritesheets, and the reason is worth
 * writing down. `waker-walk-*.png` are six frames generated independently by
 * flux-1.1-pro with no skeleton conditioning and no identity anchor, so the
 * head reshapes between frames, the character's height jumps, and the baseline
 * drifts. It does not animate; it boils.
 *
 * This rig cannot drift, by construction: it cuts SEVEN PARTS out of a SINGLE
 * source frame along authored polygons, each with a pivot, and animates them
 * with curves. There is only ever one drawing of the character, so there is
 * nothing to be inconsistent with.
 *
 * Ported from the version in `public/small-hours.html` ("walker: code cutout
 * rig, 24fps, stride-locked"), which is where this was solved the first time.
 * The maths below is that implementation's, kept deliberately faithful —
 * including the constants, which are tuned and should not be casually changed.
 *
 * What makes it read as animation rather than as a puppet:
 *
 *   - Stride lock. Phase advances by distance actually travelled
 *     (`phase += step / STRIDE`), never by time, so the feet cannot slide at
 *     any speed or any depth scale.
 *   - Secondary motion. The satchel is a damped pendulum driven by the body's
 *     acceleration; the head lags the torso lean and bobs late. Nothing moves
 *     on the same frame as its cause.
 *   - Weight. Two contacts per cycle with a vertical bob, and a squash about
 *     the feet baseline on each contact.
 *   - A 24fps pose grid while position stays continuous — the hand-drawn
 *     "animating on twos" feel. Do not smooth this out.
 *   - Idle is not a still. Breath, a slow weight shift, and the satchel
 *     settling out of its last swing.
 */
import * as Phaser from 'phaser';

/**
 * The parts are pre-baked PNGs, cut from one source frame by
 * `scripts/art/cut-walker-rig.mjs`. Keep PART_DEFS' polygons in sync with it.
 *
 * They used to be clipped at runtime with canvas + `addCanvas()`. Don't go
 * back: that upload races the first WebGL render and parts arrive torn — the
 * head came through sliced by a band of uninitialised (black) texture memory on
 * about half of all frames, and an explicit `refresh()` only reduced it.
 */
export const WALKER_RIG_PART_DIR = 'assets/sprites/waker/rig';
/** All six parts in one texture, so they share a single draw batch. */
export const WALKER_RIG_ATLAS = 'walker-rig';
export const WALKER_RIG_ATLAS_PNG = 'assets/sprites/waker/rig/walker-rig.png';
export const WALKER_RIG_ATLAS_JSON = 'assets/sprites/waker/rig/walker-rig.json';
const FRAME_SIZE = 128;

/** Feet baseline within the 128-space of the source frame. */
const BASE_Y = 123;

/**
 * Ground travel in source-space px per full gait cycle. Kills foot-slide.
 *
 * Tied to PaintedScene's WALKER_SPEED_NORM: cadence is speed / STRIDE, and a
 * natural walk sits near 1.2-1.4 cycles a second. Raise one without the other
 * and the character either shuffles or windmills.
 */
const STRIDE = 112;

/** Pose is held on a 24fps grid; position stays continuous. */
const POSE_DT = 1 / 24;

/** Speed (source px/s) treated as a full-amplitude stride. */
const FULL_STRIDE_SPEED = 165;

interface PartDef {
  n: string;
  piv: [number, number];
  poly: [number, number][];
}

/**
 * Polygons in the 128-space of the source frame; pivot is the joint the part
 * rotates about. Draw order is the array order: rear leg first, head last.
 */
const PART_DEFS: PartDef[] = [
  { n: 'rearLeg', piv: [57, 87], poly: [[36, 83], [64, 83], [66, 110], [62, 124], [48, 125], [34, 116]] },
  { n: 'frontLeg', piv: [68, 87], poly: [[60, 83], [84, 83], [97, 106], [97, 122], [76, 122], [61, 104]] },
  { n: 'torso', piv: [63, 86], poly: [[44, 34], [82, 34], [86, 62], [83, 92], [48, 92], [42, 60]] },
  { n: 'satchel', piv: [56, 57], poly: [[42, 55], [63, 55], [65, 80], [44, 80]] },
  { n: 'arm', piv: [67, 56], poly: [[58, 54], [78, 52], [82, 72], [78, 88], [64, 86], [57, 68]] },
  { n: 'head', piv: [62, 40], poly: [[28, 2], [92, 2], [90, 34], [72, 46], [48, 45], [28, 30]] },
];

export class WalkerRig {
  private readonly scene: Phaser.Scene;
  private readonly container: Phaser.GameObjects.Container;
  private readonly parts = new Map<string, Phaser.GameObjects.Image>();
  private readonly shadow: Phaser.GameObjects.Ellipse;

  /** Gait phase in [0,1). Advanced by distance, never by time. */
  private phase = 0;
  private poseClock = 0;
  private needPose = true;

  // secondary-motion state
  private satchelA = 0;
  private satchelV = 0;
  private headLag = 0;
  private leanS = 0;
  private hspeed = 0;
  private hspeedPrev = 0;
  private idleT = 0;

  private facing: 1 | -1 = 1;
  /** Base scale set by the caller; squash is applied on top, never compounded. */
  private baseScale = 1;
  private destroyed = false;

  /** Fires on each foot contact, for footstep audio. */
  public onFootfall: (() => void) | null = null;


  constructor(scene: Phaser.Scene, x: number, y: number, depth: number) {
    this.scene = scene;

    this.container = scene.add.container(x, y);
    this.container.setDepth(depth);

    this.shadow = scene.add.ellipse(0, 6, 52, 12, 0x4e2518, 0.22);
    this.container.add(this.shadow);

    for (const def of PART_DEFS) {
      const img = scene.add.image(0, 0, WALKER_RIG_ATLAS, def.n);
      // Origin at the pivot so rotation happens about the joint, and the
      // part's placement within the frame is preserved.
      img.setOrigin(def.piv[0] / FRAME_SIZE, def.piv[1] / FRAME_SIZE);
      img.setPosition(def.piv[0] - FRAME_SIZE / 2, def.piv[1] - BASE_Y);
      this.parts.set(def.n, img);
      this.container.add(img);
    }

    this.baseScale = 1;
    this.renderPose(POSE_DT, false, 0);
  }

  // ── public API ────────────────────────────────────────────────────────────

  setPosition(x: number, y: number): void {
    this.container.setPosition(x, y);
  }

  setScale(s: number): void {
    this.baseScale = s;
    this.container.scaleX = this.facing * s;
    // scaleY is owned by the pose (squash); leave it to renderPose.
  }

  setDepth(d: number): void {
    this.container.setDepth(d);
  }

  setVisible(v: boolean): void {
    this.container.setVisible(v);
  }

  /** True when the character is facing left, for anything parented to her. */
  isFacingLeft(): boolean {
    return this.facing === -1;
  }

  getContainer(): Phaser.GameObjects.Container {
    return this.container;
  }

  /**
   * Advance the rig.
   *
   * @param deltaMs frame delta
   * @param stepPx  distance actually travelled this frame, in source-space px.
   *                Pass 0 when standing still — this is what stride-locks the
   *                gait, so it must be real distance, not intended distance.
   * @param dirX    horizontal direction of travel, for facing
   */
  update(deltaMs: number, stepPx: number, dirX: number): void {
    if (this.destroyed) return;
    const dt = Math.min(50, deltaMs) / 1000;
    const moving = stepPx > 0.01;

    if (moving) {
      if (Math.abs(dirX) > 0.001) {
        const next = dirX < 0 ? -1 : 1;
        if (next !== this.facing) {
          this.facing = next;
          this.container.scaleX = Math.abs(this.container.scaleX) * this.facing;
        }
      }
      const prevPhase = this.phase;
      this.phase = (this.phase + stepPx / STRIDE) % 1;
      // Two contacts per cycle: crossing the half and wrapping the whole.
      if ((prevPhase < 0.5 && this.phase >= 0.5) || this.phase < prevPhase) {
        this.onFootfall?.();
      }
      this.hspeed = stepPx / Math.max(dt, 0.001);
    } else {
      this.hspeed = 0;
      this.phase = 0;
    }

    this.poseClock += dt;
    if (this.poseClock >= POSE_DT || this.needPose) {
      const dtPose = this.needPose ? POSE_DT : this.poseClock;
      this.poseClock = 0;
      this.needPose = false;
      this.renderPose(dtPose, moving, Math.min(1, this.hspeed / FULL_STRIDE_SPEED));
    }
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.container.destroy(true);
    this.parts.clear();
  }

  // ── the pose ──────────────────────────────────────────────────────────────

  private renderPose(dtPose: number, moving: boolean, speedNorm: number): void {
    const accel = (this.hspeed - this.hspeedPrev) / Math.max(dtPose, 0.001);
    this.hspeedPrev = this.hspeed;

    // Torso leans into the motion, and springs toward acceleration — this is
    // what makes starting and stopping read as weight rather than teleporting.
    const leanTarget =
      (moving ? 0.07 * speedNorm : 0) + Phaser.Math.Clamp(accel * 0.0009, -0.09, 0.09);
    this.leanS += (leanTarget - this.leanS) * 0.25;

    const w = 2 * Math.PI * this.phase;
    const amp = moving ? 0.4 * (0.6 + 0.4 * speedNorm) : 0;
    const legF = amp * Math.cos(w);
    const legR = -amp * Math.cos(w);
    const bob = moving ? -1.9 * Math.abs(Math.sin(w)) : 0;
    const squash = moving ? 1 - 0.028 * Math.max(0, -Math.sin(2 * w)) : 1;

    // Idle is not a still frame.
    this.idleT += dtPose;
    const breath = moving ? 0 : 0.028 * Math.sin(this.idleT * 1.5);
    const idleSway = moving ? 0 : 0.045 * Math.sin(this.idleT * 0.6);

    // Head follows through: it lags the lean and bobs late.
    this.headLag +=
      (this.leanS * 0.55 + (moving ? 0.05 * Math.sin(w - 0.9) : 0.5 * idleSway) - this.headLag) *
      0.3;

    // Satchel is a damped pendulum hung off the body's acceleration.
    const rest = -accel * 0.0016 + this.leanS * 0.8;
    this.satchelV += (-(this.satchelA - rest) * 14 - this.satchelV * 5.5) * dtPose;
    this.satchelA += this.satchelV * dtPose;

    const armA = moving ? -0.28 * Math.cos(w) - this.leanS : -this.leanS * 0.6 + 0.3 * idleSway;

    this.setPart('rearLeg', legR * 0.9, 1, 1 + 0.05 * Math.max(0, Math.cos(w + Math.PI)));
    this.setPart('frontLeg', legF, 1, 1 + 0.05 * Math.max(0, Math.cos(w)));
    this.setPart('torso', this.leanS, 1, 1 + breath);
    this.setPart('satchel', this.satchelA, 1, 1);
    this.setPart('arm', armA, 1.06, 1.06);
    this.setPart('head', this.headLag, 1, 1);

    // Bob and squash apply to the whole body, about the feet.
    for (const img of this.parts.values()) {
      img.y += bob;
    }
    this.shadow.setSize(52 + 6 * Math.abs(Math.sin(w)), 12);
    this.shadow.setAlpha(moving ? 0.2 : 0.22);
    // Squash multiplies the BASE scale. Reading the container's own scaleY back
    // and multiplying compounds every pose tick and collapses the rig.
    this.container.scaleY = this.baseScale * squash;
  }

  private setPart(name: string, rotation: number, sx: number, sy: number): void {
    const def = PART_DEFS.find((d) => d.n === name);
    const img = this.parts.get(name);
    if (!img || !def) return;
    img.setRotation(rotation);
    img.setScale(sx, sy);
    img.setPosition(def.piv[0] - FRAME_SIZE / 2, def.piv[1] - BASE_Y);
  }

}
